import React, { useState, useEffect, useRef } from 'react';
import {
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Box,
  IconButton,
  Typography,
  ListItemIcon,
  ListItemText,
  Divider,
  InputBase,
  Paper
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SearchIcon from '@mui/icons-material/Search';
import { useRTL } from '../../context/RTLContext';

interface Country {
  code: string;
  name: string;
  dialCode: string;
  flag: string;
}

// Common countries for Middle East, Africa, and global
const countries: Country[] = [
  { code: 'US', name: 'United States', dialCode: '+1', flag: '🇺🇸' },
  { code: 'GB', name: 'United Kingdom', dialCode: '+44', flag: '🇬🇧' },
  { code: 'SA', name: 'Saudi Arabia', dialCode: '+966', flag: '🇸🇦' },
  { code: 'AE', name: 'United Arab Emirates', dialCode: '+971', flag: '🇦🇪' },
  { code: 'EG', name: 'Egypt', dialCode: '+20', flag: '🇪🇬' },
  { code: 'IL', name: 'Israel', dialCode: '+972', flag: '🇮🇱' },
  { code: 'JO', name: 'Jordan', dialCode: '+962', flag: '🇯🇴' },
  { code: 'LB', name: 'Lebanon', dialCode: '+961', flag: '🇱🇧' },
  { code: 'MA', name: 'Morocco', dialCode: '+212', flag: '🇲🇦' },
  { code: 'DZ', name: 'Algeria', dialCode: '+213', flag: '🇩🇿' },
  { code: 'TN', name: 'Tunisia', dialCode: '+216', flag: '🇹🇳' },
  { code: 'LY', name: 'Libya', dialCode: '+218', flag: '🇱🇾' },
  { code: 'SD', name: 'Sudan', dialCode: '+249', flag: '🇸🇩' },
  { code: 'IQ', name: 'Iraq', dialCode: '+964', flag: '🇮🇶' },
  { code: 'KW', name: 'Kuwait', dialCode: '+965', flag: '🇰🇼' },
  { code: 'QA', name: 'Qatar', dialCode: '+974', flag: '🇶🇦' },
  { code: 'BH', name: 'Bahrain', dialCode: '+973', flag: '🇧🇭' },
  { code: 'OM', name: 'Oman', dialCode: '+968', flag: '🇴🇲' },
  { code: 'YE', name: 'Yemen', dialCode: '+967', flag: '🇾🇪' },
  { code: 'PS', name: 'Palestine', dialCode: '+970', flag: '🇵🇸' },
  { code: 'TR', name: 'Turkey', dialCode: '+90', flag: '🇹🇷' },
  { code: 'ZA', name: 'South Africa', dialCode: '+27', flag: '🇿🇦' },
  { code: 'NG', name: 'Nigeria', dialCode: '+234', flag: '🇳🇬' },
  { code: 'KE', name: 'Kenya', dialCode: '+254', flag: '🇰🇪' },
  { code: 'ET', name: 'Ethiopia', dialCode: '+251', flag: '🇪🇹' },
  { code: 'GH', name: 'Ghana', dialCode: '+233', flag: '🇬🇭' },
  { code: 'FR', name: 'France', dialCode: '+33', flag: '🇫🇷' },
  { code: 'DE', name: 'Germany', dialCode: '+49', flag: '🇩🇪' },
  { code: 'IN', name: 'India', dialCode: '+91', flag: '🇮🇳' },
  { code: 'PK', name: 'Pakistan', dialCode: '+92', flag: '🇵🇰' }
].sort((a, b) => a.name.localeCompare(b.name));

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  helperText?: string;
  label?: string;
  fullWidth?: boolean;
  required?: boolean;
}

const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  error,
  helperText,
  label = 'Phone Number',
  fullWidth = true,
  required = false
}) => {
  const { language, direction } = useRTL();
  const [selectedCountry, setSelectedCountry] = useState<Country>(countries[0]);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Detect country based on language
  useEffect(() => {
    const lang = language.split('-')[0];
    let defaultCountry = countries[0]; // Default to US

    switch (lang) {
      case 'ar':
        // Try to detect specific Arabic region
        if (language.includes('SA')) defaultCountry = countries.find(c => c.code === 'SA') || defaultCountry;
        else if (language.includes('EG')) defaultCountry = countries.find(c => c.code === 'EG') || defaultCountry;
        else if (language.includes('AE')) defaultCountry = countries.find(c => c.code === 'AE') || defaultCountry;
        else defaultCountry = countries.find(c => c.code === 'SA') || defaultCountry; // Default to Saudi
        break;
      case 'he':
        defaultCountry = countries.find(c => c.code === 'IL') || defaultCountry;
        break;
      case 'fr':
        defaultCountry = countries.find(c => c.code === 'FR') || defaultCountry;
        break;
      default:
        break;
    }

    setSelectedCountry(defaultCountry);
  }, [language]);

  // Parse value prop if it contains country code
  useEffect(() => {
    if (value && value.startsWith('+')) {
      // Find matching country by dial code
      const matchingCountry = countries.find(c => value.startsWith(c.dialCode));
      if (matchingCountry) {
        setSelectedCountry(matchingCountry);
        setPhoneNumber(value.replace(matchingCountry.dialCode, '').trim());
      } else {
        setPhoneNumber(value);
      }
    } else {
      setPhoneNumber(value);
    }
  }, [value]);

  const handleCountryClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCountryClose = () => {
    setAnchorEl(null);
    setSearchQuery('');
  };

  const handleCountrySelect = (country: Country) => {
    setSelectedCountry(country);
    handleCountryClose();
    // Update the full phone number
    const fullNumber = phoneNumber ? `${country.dialCode} ${phoneNumber}` : '';
    onChange(fullNumber);
    // Focus back on phone input
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newNumber = event.target.value.replace(/[^\d\s-()]/g, ''); // Allow only digits, spaces, hyphens, and parentheses
    setPhoneNumber(newNumber);
    
    // Combine country code and phone number
    const fullNumber = newNumber ? `${selectedCountry.dialCode} ${newNumber}` : '';
    onChange(fullNumber);
  };

  const filteredCountries = countries.filter(country =>
    country.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    country.dialCode.includes(searchQuery) ||
    country.code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box>
      <TextField
        fullWidth={fullWidth}
        label={label}
        value={phoneNumber}
        onChange={handlePhoneChange}
        error={error}
        helperText={helperText}
        required={required}
        placeholder="123-456-7890"
        inputRef={inputRef}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start" sx={{ mr: 0 }}>
              <Box
                onClick={handleCountryClick}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  p: 0.5,
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  },
                  transition: 'background-color 0.2s',
                  userSelect: 'none'
                }}
              >
                <Typography sx={{ fontSize: '1.3em', mr: 0.5 }}>
                  {selectedCountry.flag}
                </Typography>
                <Typography sx={{ fontSize: '0.9em', fontWeight: 500, mr: 0.5 }}>
                  {selectedCountry.dialCode}
                </Typography>
                <ArrowDropDownIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Divider orientation="vertical" flexItem sx={{ mx: 1, height: 24 }} />
              </Box>
            </InputAdornment>
          )
        }}
        inputProps={{
          inputMode: 'tel',
          pattern: '[0-9\\s\\-()]*'
        }}
        sx={{
          '& .MuiInputBase-input': {
            pl: 0.5
          }
        }}
      />

      {/* Country Selection Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCountryClose}
        PaperProps={{
          sx: {
            maxHeight: 400,
            width: '90vw',
            maxWidth: 360,
            mt: 1
          }
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: direction === 'rtl' ? 'right' : 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: direction === 'rtl' ? 'right' : 'left',
        }}
      >
        {/* Search Box */}
        <Box sx={{ px: 2, py: 1, position: 'sticky', top: 0, backgroundColor: 'background.paper', zIndex: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search country..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }}
            autoFocus
            onClick={(e) => e.stopPropagation()}
          />
        </Box>
        
        <Divider />

        {/* Country List */}
        {filteredCountries.length > 0 ? (
          filteredCountries.map((country) => (
            <MenuItem
              key={country.code}
              onClick={() => handleCountrySelect(country)}
              selected={country.code === selectedCountry.code}
              sx={{
                py: 1.5,
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Typography sx={{ fontSize: '1.4em', mr: 2 }}>
                  {country.flag}
                </Typography>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {country.name}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  {country.dialCode}
                </Typography>
              </Box>
            </MenuItem>
          ))
        ) : (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No countries found
            </Typography>
          </Box>
        )}
      </Menu>
    </Box>
  );
};

export default PhoneInput;